import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Loader2 } from "lucide-react";

const fmtEur = (n) => `${Number(n || 0).toLocaleString("es-ES", { maximumFractionDigits: 0 })} €`;
const fmtNum = (n) => Number(n || 0).toLocaleString("es-ES");

/** Control de cumplimiento de los traslados aplicados (iter-133). */
export const TransferComplianceCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/catalog/bulk-transfer-compliance")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="mb-6 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Comprobando los traslados aplicados…
      </div>
    );
  }
  if (!data?.has_batches || !data.summary) return null;

  const s = data.summary;
  const breach = s.molecules_breaching > 0;

  return (
    <div
      className={`mb-6 rounded-2xl border-2 p-5 ${breach ? "border-amber-300 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}
      data-testid="transfer-compliance-card"
    >
      <div className="flex items-start gap-3">
        {breach
          ? <AlertTriangle className="h-7 w-7 text-amber-600 shrink-0" />
          : <CheckCircle2 className="h-7 w-7 text-emerald-600 shrink-0" />}
        <div className="flex-1">
          <div className={`font-display text-lg sm:text-xl font-bold ${breach ? "text-amber-900" : "text-emerald-900"}`}>
            {breach
              ? `${s.molecules_breaching} molécula(s) trasladadas se siguen comprando a otro laboratorio`
              : "Todos los traslados se están cumpliendo"}
          </div>
          <p className={`text-sm mt-1 ${breach ? "text-amber-900/80" : "text-emerald-900/80"}`}>
            {breach ? (
              <>
                {fmtNum(s.units_off_target)} unidades y <strong>{fmtEur(s.euros_off_target)}</strong> fuera del
                laboratorio acordado en <strong>{data.study?.label}</strong>.
                {s.still_old_lab > 0 && <> En {s.still_old_lab} de ellas se sigue comprando al <strong>laboratorio antiguo</strong>.</>}
              </>
            ) : (
              <>Revisadas {s.molecules_with_data} de {s.molecules_transferred} moléculas trasladadas en {data.study?.label}.</>
            )}
          </p>
        </div>
        {breach && (
          <button
            onClick={() => setOpen((v) => !v)}
            className="text-xs font-bold text-amber-900 inline-flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-amber-100"
            data-testid="transfer-compliance-toggle"
          >
            {open ? "Ocultar" : "Ver detalle"}
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        )}
      </div>

      {open && breach && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs bg-white rounded-lg overflow-hidden">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
              <tr>
                <th className="text-left p-2.5">Molécula (G.H.)</th>
                <th className="text-left p-2.5">Debería comprarse a</th>
                <th className="text-left p-2.5">Se está comprando a</th>
                <th className="text-right p-2.5">Uds fuera</th>
                <th className="text-right p-2.5">€ fuera</th>
                <th className="text-right p-2.5">Cumplimiento</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.gh} className="border-t border-slate-100" data-testid={`compliance-row-${r.gh}`}>
                  <td className="p-2.5 font-medium text-slate-900">{r.gh}</td>
                  <td className="p-2.5 text-slate-700">{r.target_lab}</td>
                  <td className="p-2.5 text-slate-700">
                    {r.labs_off_target.map((l) => (
                      <span key={l.laboratorio} className="inline-block mr-2">
                        {l.laboratorio} <span className="text-slate-400">({fmtNum(l.units)} uds)</span>
                        {r.still_old_lab && r.prev_lab
                          && l.laboratorio.toUpperCase() === r.prev_lab.toUpperCase() && (
                          <span className="ml-1 text-[10px] font-bold text-rose-600">antiguo</span>
                        )}
                      </span>
                    ))}
                  </td>
                  <td className="p-2.5 text-right font-mono-num">{fmtNum(r.units_off_target)}</td>
                  <td className="p-2.5 text-right font-mono-num font-bold">{fmtEur(r.euros_off_target)}</td>
                  <td className="p-2.5 text-right font-mono-num">{r.compliance_pct} %</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-amber-900/70 mt-2">
            Se compara el genérico dispensado en el último estudio con el laboratorio al que trasladaste cada molécula.
            Si el cambio fue a mitad de mes, es normal ver parte del volumen en el laboratorio anterior.
          </p>
        </div>
      )}
    </div>
  );
};
